import { Box, SxProps, Tab, Tabs, Theme } from '@mui/material';
import { ReactNode, useState } from 'react';

interface TabPanelProps {
 children: ReactNode;
 index: number;
 value: number;
}
interface TabItemProps {
 id: string;
 label: string;
 content: ReactNode;
 disabled?: boolean;
}
interface EditTabsProps {
 tabs: TabItemProps[];
 defaultTab?: number;
 variant?: 'fullWidth' | 'standard' | 'scrollable';
 ariaLabel?: string;
 sx?: SxProps<Theme>;
 tabSx?: SxProps<Theme>;
 panelSx?: SxProps<Theme>;
 onChange?: (newValue: number) => void;
}

function a11yProps(index: number) {
 return {
  id: `edittabs-tab-${index}`,
  'aria-controls': `edittabs-tabpanel-${index}`,
 };
}

function CustomTabPanel(props: TabPanelProps) {
 const { children, value, index, ...other } = props;

 return (
  <div
   role="tabpanel"
   hidden={value !== index}
   id={`edittabs-tabpanel-${index}`}
   aria-labelledby={`edittabs-tab-${index}`}
   {...other}
  >
   {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
 );
}

const defaultTabSx = {
 '& .Mui-selected': {
  backgroundColor: (theme: Theme) => theme.palette.kemek.white,
 },
 '& .MuiButtonBase-root.MuiTab-root': {
  pt: '12px',
  pb: '12px',
  fontSize: '14px',
 },
};

const defaultPanelSx = {
 p: 0,
};

const EditTabs = ({
 tabs,
 defaultTab = 0,
 variant = 'fullWidth',
 ariaLabel = 'Tabs',
 sx,
 tabSx,
 panelSx,
 onChange,
}: EditTabsProps) => {
 const [tab, setTab] = useState(defaultTab);

 const handleChange = (event: React.SyntheticEvent, newValue: number) => {
  setTab(newValue);
  onChange?.(newValue);
 };

 return (
  <Box>
   <Box
    sx={{
     borderBottom: 1,
     borderBottomColor: (theme: Theme) => theme.palette.kemek.lightestGray,
     mt: '-1px',
     ...sx,
    }}
   >
    <Tabs
     variant={variant}
     value={tab}
     onChange={handleChange}
     aria-label={ariaLabel}
     sx={{
      ...defaultTabSx,
      ...tabSx,
     }}
    >
     {tabs.map((tabItem, index) => (
      <Tab
       key={tabItem.id}
       label={tabItem.label}
       disabled={tabItem.disabled}
       {...a11yProps(index)}
      />
     ))}
    </Tabs>
   </Box>

   {tabs.map((tabItem, index) => (
    <CustomTabPanel key={tabItem.id} value={tab} index={index}>
     <Box sx={{ ...defaultPanelSx, ...panelSx }}>{tabItem.content}</Box>
    </CustomTabPanel>
   ))}
  </Box>
 );
};

export default EditTabs;
